import { useState, useRef, useLayoutEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';
import { AsyncState } from '@/components/ui/AsyncState';
import type { AccountBalanceDetail } from '@/models/account/types';
import type { DateFilter } from '@/controllers/clients/clientCard';

type DescriptionCellProps = {
  text: string;
  isExpanded: boolean;
  onToggle: () => void;
};

// eslint-disable-next-line react-refresh/only-export-components
function DescriptionCell({ text, isExpanded, onToggle }: DescriptionCellProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [expandedHeight, setExpandedHeight] = useState(20);
  const COLLAPSED_H = 20;
  const ease = [0.4, 0, 0.2, 1] as const;

  // useLayoutEffect (не useEffect): измеряем DOM до отрисовки,
  // иначе высота мигает при первом рендере.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const span = container.querySelector('span');
    if (!span) return;
    span.classList.remove('line-clamp-1');
    const h = container.scrollHeight;
    span.classList.add('line-clamp-1');
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setExpandedHeight(h);
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setIsTruncated(h > COLLAPSED_H + 2);
  }, [text]);

  const canToggle = isTruncated || isExpanded;

  return (
    <div
      className={cn('col-span-3 flex items-start gap-1 min-w-0', canToggle && 'cursor-pointer')}
      onClick={
        canToggle
          ? (e) => {
              e.stopPropagation();
              onToggle();
            }
          : undefined
      }
    >
      <motion.div
        ref={containerRef}
        className='overflow-hidden flex-1 min-w-0'
        animate={{ height: isExpanded ? expandedHeight : COLLAPSED_H }}
        initial={false}
        transition={{ duration: 0.3, ease }}
      >
        <span
          className={cn(
            'text-sm text-muted-foreground block wrap-break-word',
            !isExpanded && 'line-clamp-1'
          )}
        >
          {text}
        </span>
      </motion.div>
      {canToggle && (
        <motion.span
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.3, ease }}
          className='shrink-0 mt-1 flex'
        >
          <ChevronDown className='w-3 h-3 text-muted-foreground/40' />
        </motion.span>
      )}
    </div>
  );
}

type Props = {
  items: AccountBalanceDetail[];
  currency: string;
  isLoading: boolean;
  error: string | null;
  dateFilter: DateFilter;
  expandedRows: string[];
  onDateFilterChange: (filter: DateFilter) => void;
  onToggleExpanded: (rowKey: string) => void;
};

export const BalanceDetailTab = observer(function BalanceDetailTab({
  items,
  currency,
  isLoading,
  error,
  dateFilter,
  expandedRows,
  onDateFilterChange,
  onToggleExpanded,
}: Props) {
  return (
    <div>
      <div className='flex items-center justify-between mb-4 flex-wrap gap-2'>
        <h3 className='text-sm font-medium text-foreground'>Детализация баланса</h3>
        <div className='relative'>
          <select
            value={dateFilter}
            onChange={(e) => onDateFilterChange(e.target.value as DateFilter)}
            className='appearance-none pl-3 pr-8 py-1.5 rounded-lg bg-muted/50 border border-border/50 text-sm text-foreground outline-none cursor-pointer'
          >
            <option value='1month'>За месяц</option>
            <option value='3month'>За 3 месяца</option>
            <option value='6month'>За 6 месяцев</option>
            <option value='year'>За год</option>
          </select>
          <ChevronDown className='absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none' />
        </div>
      </div>

      <AsyncState
        isLoading={isLoading}
        error={error}
        isEmpty={items.length === 0}
        emptyMessage='Операций не найдено'
      >
        <div className='border border-border/30 rounded-xl overflow-hidden'>
          <div className='grid grid-cols-12 gap-3 px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase tracking-wider bg-muted/20 border-b border-border/20'>
            <div className='col-span-2'>Дата</div>
            <div className='col-span-2'>Документ</div>
            <div className='col-span-1'>Тип</div>
            <div className='col-span-3'>Описание</div>
            <div className='col-span-2 text-right'>Сумма</div>
            <div className='col-span-2 text-right'>Баланс</div>
          </div>
          <div className='divide-y divide-border/20'>
            {items.map((item) => {
              const rowKey = `${item.num_doc}-${item.date}`;
              const isExpanded = expandedRows.includes(rowKey);
              return (
                <div
                  key={rowKey}
                  className='grid grid-cols-12 gap-3 px-4 py-3 items-start hover:bg-muted/20 transition-colors'
                >
                  <div className='col-span-2 font-mono text-xs text-muted-foreground pt-0.5'>
                    {format(parseISO(item.date), 'dd.MM.yyyy')}
                  </div>
                  <div className='col-span-2 font-mono text-xs text-muted-foreground truncate pt-0.5'>
                    {item.num_doc || '—'}
                  </div>
                  <div className='col-span-1 text-xs text-muted-foreground truncate pt-0.5'>
                    {item.ptype || '—'}
                  </div>
                  <DescriptionCell
                    text={item.description || item.comment || '—'}
                    isExpanded={isExpanded}
                    onToggle={() => onToggleExpanded(rowKey)}
                  />
                  <div
                    className={cn(
                      'col-span-2 text-sm font-semibold text-right pt-0.5',
                      item.balance > 0
                        ? 'text-green-400'
                        : item.balance < 0
                          ? 'text-red-400'
                          : 'text-muted-foreground'
                    )}
                  >
                    {item.balance > 0 ? '+' : ''}
                    {item.balance.toLocaleString('ru-RU')} {currency}
                  </div>
                  <div className='col-span-2 text-sm text-right font-mono text-muted-foreground pt-0.5'>
                    {item.full_summ.toLocaleString('ru-RU')} {currency}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </AsyncState>
    </div>
  );
});
